import { useCallback } from 'react';

import { useAxios } from '@/hooks/useAxios';
import { useCreateCustomer } from '@/hooks/useCustomerType';
import { useRbacEnabled } from '@/hooks/useRbacEnabled';
import { useUpdateSubscription } from '@/hooks/useSubscription';
import { SubmitConfig } from '@/sections/customerManagement/components/modals/AddCustomerModal/AddCustomerModal.types';
import {
  generateSecurePassword,
  generateUsername,
} from '@/sections/customerManagement/components/modals/AddCustomerModal/utils';
import { EDITABLE_TYPES } from '@/sections/customerManagement/constants/addCustomerModal';
import {
  buildSubscriptionConfiguration,
  createSubscriptionIfNeeded,
} from '@/sections/customerManagement/utils';
import { parseBugBountyUrl } from '@/sections/customerManagement/utils/bugBountyUrl';
import { configureBugBountyIntegration } from '@/sections/customerManagement/utils/configureBugBountyIntegration';
import { useAuth } from '@/state/auth';
import { WeekDay } from '@/types';

/**
 * Hook for handling customer creation submission
 */
export const useCustomerSubmit = ({
  formData,
  enabledFlags,
  onSuccess,
  onClose,
}: SubmitConfig) => {
  const createCustomer = useCreateCustomer();
  const { mutateAsync: updateSubscription } = useUpdateSubscription();
  const axios = useAxios();
  const { getToken } = useAuth();
  const enableRbac = useRbacEnabled();

  const handleSubmit = useCallback(async () => {
    // Generate credentials
    const username = generateUsername(formData.settings_display_name);
    const password = generateSecurePassword();

    // Pass the subscription window into onboard so the backend can pre-freeze a
    // future-dated account before it seeds/scans. Only send it when a real start
    // date exists (eligible types); the full subscription blob is still written
    // post-onboard by createSubscriptionIfNeeded (same window). Omit an empty
    // endDate so the backend's date validator doesn't reject "".
    const subscriptionConfig = buildSubscriptionConfiguration(formData);
    const onboardSubscription =
      subscriptionConfig && subscriptionConfig.startDate
        ? {
            startDate: subscriptionConfig.startDate,
            ...(subscriptionConfig.endDate
              ? { endDate: subscriptionConfig.endDate }
              : {}),
          }
        : undefined;

    // Create the customer
    await createCustomer.mutateAsync({
      subscription: onboardSubscription,
      username,
      password,
      settings_display_name: formData.settings_display_name,
      customer_type: formData.customer_type || undefined,
      profile_picture: formData.profile_picture,
      enabled_flags: enabledFlags,
      plextrac_id: formData.plextrac_id || undefined,
      collaborators:
        formData.collaborators.length > 0
          ? formData.collaborators.map(item => ({
              email: item.collaborator,
              role: enableRbac ? item.role : 'admin',
            }))
          : [],
      parent_account: formData.parent_account || undefined,
      allowed_domains:
        formData.allowed_domains.length > 0
          ? formData.allowed_domains
          : undefined,
      scan_cadence: {
        frequency: 'weekly',
        dayOfWeek: (EDITABLE_TYPES as readonly string[]).includes(
          formData.customer_type ?? ''
        )
          ? formData.scan_day
          : WeekDay.Monday,
      },
    });

    // Create subscription if applicable
    const token = await getToken();
    await createSubscriptionIfNeeded({
      formData,
      username,
      updateSubscription,
    });

    // Auto-configure bug bounty platform integration
    if (
      formData.customer_type === 'BUG_BOUNTY' &&
      formData.hackerone_program_url
    ) {
      const parsed = parseBugBountyUrl(formData.hackerone_program_url);

      if (parsed) {
        await configureBugBountyIntegration(
          axios,
          username,
          parsed.platform,
          parsed.handle,
          token
        );
      }
    }

    // Trigger success callback with created customer email and close modal
    await onSuccess?.(username);
    onClose();
  }, [
    formData,
    enabledFlags,
    enableRbac,
    createCustomer,
    updateSubscription,
    axios,
    getToken,
    onSuccess,
    onClose,
  ]);

  return {
    handleSubmit,
    isPending: createCustomer.isPending,
    isError: createCustomer.isError,
    error: createCustomer.error,
  };
};
